import { Search } from "lucide-react";
import { type FC, type HTMLAttributes, type InputHTMLAttributes, type ReactNode } from "react";
import "./DashboardDataTable.scss";
import { dashboardDataTable } from "./DashboardDataTableClasses";
import TablePagePagination from "../TablePagePagination";

export { dashboardDataTable };
export type { DashboardDataTableClassKey } from "./DashboardDataTableClasses";

export interface DashboardTableColumn<Row> {
  key: string;
  header: ReactNode;
  className?: string;
  width?: string;
  // eslint-disable-next-line no-unused-vars
  render: (row: Row, index: number) => ReactNode;
}

interface DashboardDataTableProps<Row> {
  columns: Array<DashboardTableColumn<Row>>;
  rows: Row[];
  // eslint-disable-next-line no-unused-vars
  rowKey: (row: Row, index: number) => string;
  currentPage: number;
  totalPages: number;
  onPreviousPage: () => void;
  onNextPage: () => void;
}

export const DashboardDataTableCard: FC<
  HTMLAttributes<HTMLDivElement>
> = ({ className = "", children, ...rest }) => (
  <div className={`${dashboardDataTable.card} ${className}`.trim()} {...rest}>
    {children}
  </div>
);

function DashboardDataTable<Row>({
  columns,
  rows,
  rowKey,
  currentPage,
  totalPages,
  onPreviousPage,
  onNextPage,
}: DashboardDataTableProps<Row>) {
  return (
    <DashboardDataTableCard>
      <div className={dashboardDataTable.scroll}>
        <table className={dashboardDataTable.table}>
          <colgroup>
            {columns.map(column => (
              <col
                key={column.key}
                style={column.width ? { width: column.width } : undefined}
              />
            ))}
          </colgroup>
          <thead>
            <tr>
              {columns.map(column => (
                <th key={column.key} className={column.className}>
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={rowKey(row, index)}>
                {columns.map(column => (
                  <td key={column.key} className={column.className}>
                    {column.render(row, index)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <TablePagePagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPrevious={onPreviousPage}
        onNext={onNextPage}
      />
    </DashboardDataTableCard>
  );
}

type PanelProps = HTMLAttributes<HTMLElement> & {
  as?: "article" | "section";
};

export const DashboardDataTablePanel: FC<PanelProps> = ({
  as: Tag = "article",
  className = "",
  children,
  ...rest
}) => (
  <Tag className={`${dashboardDataTable.panel} ${className}`.trim()} {...rest}>
    {children}
  </Tag>
);

export const DashboardDataTablePanelHeader: FC<
  HTMLAttributes<HTMLDivElement>
> = ({ className = "", children, ...rest }) => (
  <div
    className={`${dashboardDataTable.panelHeader} ${className}`.trim()}
    {...rest}
  >
    {children}
  </div>
);

export const DashboardDataTableSearch: FC<
  Omit<InputHTMLAttributes<HTMLInputElement>, "type">
> = ({ className = "", ...rest }) => (
  <label className={dashboardDataTable.search}>
    <Search size={16} aria-hidden />
    <input type="search" className={className} {...rest} />
  </label>
);

export const DashboardDataTableEmptyState: FC<{
  title?: ReactNode;
  description?: ReactNode;
}> = ({ title = "No data available", description = "No rows match the current filters." }) => (
  <div className={dashboardDataTable.emptyState} role="status">
    <strong className={dashboardDataTable.emptyStateTitle}>{title}</strong>
    {description ? (
      <span className={dashboardDataTable.emptyStateDescription}>{description}</span>
    ) : null}
  </div>
);

export const DashboardDataTableScroll: FC<
  HTMLAttributes<HTMLDivElement>
> = ({ className = "", children, ...rest }) => (
  <div className={`${dashboardDataTable.scroll} ${className}`.trim()} {...rest}>
    {children}
  </div>
);

export const DashboardDataTableTable: FC<
  HTMLAttributes<HTMLTableElement>
> = ({ className = "", children, ...rest }) => (
  <table className={`${dashboardDataTable.table} ${className}`.trim()} {...rest}>
    {children}
  </table>
);

export default DashboardDataTable;
